<template>
  <div>
    <app-cells>
      <h1 class="title">HESLB Loans</h1>
      <app-button @click.native="onShowModal">Create Loan</app-button>
      <app-button @click.native="onShowExportModal">Export</app-button>
    </app-cells>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Total Current Balance:</span> <span>{{ loans_summary.total_current_balance.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Total Amount:</span> <span>{{ loans_summary.total_initial_amount.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Total Deducted:</span> <span>{{ loans_summary.total_deducted.toLocaleString() }} TSh</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Active Loans:</span> <span>{{ loans_summary.active_loans_count }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Fully Paid Loans:</span> <span>{{ loans_summary.fully_paid_loans_count }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Inactive Loans:</span> <span>{{ loans_summary.inactive_loans_count }}</span>
            </div>
          </div>
        </div>
      </div>
      <app-tabs :tabs="tabs"/>
      <transition mode="out-in" name="fade">
        <router-view ref="activeLoans" @edit-loan="onEditLoan"/>
      </transition>
    </template>
    <modal
      name="create_loans"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('create_loans')" class="popup__close" type="button"></button>
        <div class="popup__title">Create HESLB loan</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Employee" required>
              <app-select
                @search="onEmployeeSearch"
                v-model="form.employee"
                :options="employee_options"
                :clearable="false"
                label="full_name"
                placeholder="Employee"
                :error="$v.form.employee.$error"
              />
              <template #error>
                <div v-if="$v.form.employee.$dirty && !$v.form.employee.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Profile / account" required label-for="profile">
              <app-input
                v-model="form.profile"
                :error="$v.form.profile.$error"
                id="profile"
                placeholder="Profile / account"
                name="profile"
              />
              <template #error>
                <div v-if="$v.form.profile.$dirty && !$v.form.profile.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Current balance" label-for="current_balance" required>
              <app-fin-number
                v-model="form.current_balance"
                id="current_balance"
                name="current_balance"
                :error="$v.form.current_balance.$error"
                placeholder="0"
              />
              <template #error>
                <div v-if="$v.form.current_balance.$dirty && !$v.form.current_balance.required">Required field</div>
                <div v-if="$v.form.current_balance.$dirty && !$v.form.current_balance.greaterThanZero">Current balance must be greater than 0</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="loan_create" type="submit" :disabled="$v.form.$error">Create</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="edit_loan_profile"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('edit_loan_profile')" class="popup__close" type="button"></button>
        <div class="popup__title">Edit HESLB loan</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckEditForm">
            <app-form-group label="Profile / account" required label-for="edit_loan_profile">
              <app-input
                id="edit_loan_profile"
                v-model="edit_form.profile"
                :error="$v.edit_form.profile.$error"
                placeholder="Profile / account"
                name="edit_loan_profile"
              />
              <template #error>
                <div v-if="$v.edit_form.profile.$dirty && !$v.edit_form.profile.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="loan_edit" type="submit" :disabled="$v.edit_form.$error">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="export_loans"
      :width="760"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      @closed="resetExport"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="closeExportModal" class="popup__close" type="button"></button>
        <div class="popup__title">Export HESLB deductions</div>
        <div class="popup__body heslb-export__body">
          <form @submit.prevent="onCheckExportForm">
            <app-form-group label="Month" required>
              <app-date-picker
                v-model="export_form.month"
                value-type="YYYY-MM-DD"
                format="MMM YYYY"
                type="month"
                placeholder="Select month"
                :clearable="false"
                :error="$v.export_form.month.$error"
                @input="onExportMonthChange"
              />
              <template #error>
                <div v-if="$v.export_form.month.$dirty && !$v.export_form.month.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="loan_export" type="submit" :disabled="$v.export_form.$error">Export</app-button>
            </app-cells>
          </form>
          <div class="heslb-export__result">
            <div v-if="export_loading" class="heslb-export__placeholder">
              <img alt="" src="@/assets/img/preloader.svg">
            </div>
            <div v-else-if="export_error" class="heslb-export__alert heslb-export__alert--error">
              <div class="heslb-export__alert-title">Error</div>
              <div>{{ export_error }}</div>
            </div>
            <template v-else-if="export_result">
              <div class="heslb-export__title">Files</div>
              <div class="heslb-export__files">
                <a
                  v-for="file in export_result.files"
                  :key="file.url"
                  :href="file.url"
                  :download="file.filename"
                  class="heslb-export__file"
                >
                  <span class="heslb-export__file-text">
                    <span class="heslb-export__file-company">{{ file.company }}</span>
                    <span class="heslb-export__file-name">{{ file.filename }}</span>
                    <span class="heslb-export__file-rows">{{ file.rows_count }} {{ file.rows_count === 1 ? 'row' : 'rows' }}</span>
                  </span>
                  <span class="heslb-export__file-download">Download</span>
                </a>
              </div>
              <div v-if="export_result.warnings.length" class="heslb-export__alert heslb-export__alert--warning">
                <div class="heslb-export__alert-title">Warnings</div>
                <ul class="heslb-export__warnings">
                  <li v-for="warning in export_result.warnings" :key="warning">{{ warning }}</li>
                </ul>
              </div>
            </template>
            <div v-else class="heslb-export__placeholder">
              Select a month and press Export to get the files
            </div>
          </div>
        </div>
        <app-cells position="center" :indent="false">
          <app-button theme="dark" @click.native="closeExportModal">Close</app-button>
        </app-cells>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getEmployeesHeslbLoansExport, getEmployeesHeslbLoansSummary, getEmployeesListSearch,
  patchEmployeeHeslbLoan, postEmployeeHeslbLoans
} from '@/http'
import AppTabs from '@/components/AppTabs'
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'

export default {
  name: 'HESLBLoansLayout',
  components: {AppTabs},
  data() {
    return {
      tabs: [
        {name: 'employees-heslb-loans', label: 'Active'},
        {name: 'employees-heslb-loans-paid', label: 'Fully paid'},
        {name: 'employees-heslb-loans-inactive', label: 'Inactive'}
      ],
      loans_summary: {},
      page_loaded: false,
      form: {},
      editing_loan_id: null,
      edit_form: { profile: '' },
      export_form: {},
      export_loading: false,
      export_result: null,
      export_error: '',
      employee_options: [],
    }
  },
  validations: {
    form: {
      employee: { required },
      profile: { required },
      current_balance: {
        required,
        greaterThanZero(value) {
          return !value || Number(value) > 0
        }
      }
    },
    edit_form: {
      profile: { required }
    },
    export_form: {
      month: { required }
    }
  },
  created() {
    getEmployeesHeslbLoansSummary().then(response => {
      this.loans_summary = response.data
      this.page_loaded = true
    })
  },
  methods: {
    onEditLoan(row) {
      this.editing_loan_id = row.id
      this.edit_form = { profile: row.profile || '' }
      this.$v.edit_form.$reset()
      this.$modal.show('edit_loan_profile')
    },
    onCheckEditForm() {
      this.edit_form.profile = this.edit_form.profile.trim()
      this.$v.edit_form.$touch()
      if (this.$v.edit_form.$invalid) return

      this.$refs.loan_edit.preload = true
      patchEmployeeHeslbLoan(this.editing_loan_id, { profile: this.edit_form.profile })
        .then(() => {
          if (this.$refs.activeLoans && this.$refs.activeLoans.fetchItems) {
            this.$refs.activeLoans.fetchItems()
          }
          this.$notify({ type: 'success', title: 'Loan updated' })
          this.$modal.hide('edit_loan_profile')
        })
        .catch(() => {})
        .finally(() => { this.$refs.loan_edit.preload = false })
    },
    onShowModal() {
      this.form = {}
      this.$v.form.$reset()
      this.$modal.show('create_loans')
    },
    onShowExportModal() {
      this.export_form = {}
      this.$v.export_form.$reset()
      this.resetExport()
      this.$modal.show('export_loans')
    },
    closeExportModal() {
      this.$modal.hide('export_loans')
    },
    resetExport() {
      this.export_loading = false
      this.export_result = null
      this.export_error = ''
    },
    onExportMonthChange() {
      // files of the previous month must not stay next to the newly selected one
      if (!this.export_loading) this.resetExport()
    },
    onCheckExportForm() {
      this.$v.export_form.$touch()
      if (!this.$v.export_form.$invalid) this.sendExportForm()
    },
    sendExportForm() {
      this.resetExport()
      this.export_loading = true
      this.$refs.loan_export.preload = true
      getEmployeesHeslbLoansExport({month: this.export_form.month})
        .then(response => {
          this.export_result = {
            files: response.data.files || [],
            warnings: response.data.warnings || [],
          }
        })
        .catch(error => {
          const details = error.response && error.response.data && error.response.data.details
          this.export_error = details || 'Unable to create the export files. Try again or contact your administrator.'
        })
        .finally(() => {
          this.export_loading = false
          if (this.$refs.loan_export) this.$refs.loan_export.preload = false
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.loan_create.preload = true
      postEmployeeHeslbLoans({...this.form, employee: this.form.employee.id})
        .then(() => {
          if (this.$refs.activeLoans && this.$refs.activeLoans.fetchItems) {
            this.$refs.activeLoans.fetchItems()
          }
          this.$notify({
            type: 'success',
            title: 'Loan created',
          })
          this.$modal.hide('create_loans')
        })
        .catch(() => { this.$refs.loan_create.preload = false })
    },
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployee(loading, search, this)
    },
    searchEmployee: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.employee_options = []
      } else {
        getEmployeesListSearch({search}).then(response => {
          vm.employee_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>

<style lang="sass">
.heslb-export__body
  display: grid
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr)
  gap: 24px
  margin-bottom: 16px

.heslb-export__result
  min-height: 200px
  padding: 16px
  border: 1px solid #d9e0e8
  border-radius: 8px
  background: #f8fafc

.heslb-export__title
  margin-bottom: 10px
  color: #606770
  font-weight: 700
  text-transform: uppercase

.heslb-export__placeholder
  display: flex
  align-items: center
  justify-content: center
  height: 100%
  min-height: 168px
  color: #747b84
  text-align: center

  img
    width: 36px
    height: 36px

.heslb-export__files
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr))
  gap: 12px

.heslb-export__file
  display: flex
  flex-wrap: wrap
  align-items: center
  gap: 10px
  padding: 12px
  border: 1px solid #d9e0e8
  border-radius: 8px
  background: #fff
  color: inherit
  text-decoration: none
  transition: border-color 0.2s

  &:hover
    border-color: #77b017

    .heslb-export__file-download
      text-decoration: underline

.heslb-export__file-text
  display: flex
  flex: 1
  flex-direction: column
  min-width: 0

.heslb-export__file-company
  font-weight: 700

.heslb-export__file-name
  overflow: hidden
  color: #747b84
  font-size: 12px
  text-overflow: ellipsis

.heslb-export__file-rows
  color: #747b84
  font-size: 12px

.heslb-export__file-download
  width: 100%
  color: #77b017
  font-weight: 700
  text-align: right

.heslb-export__alert
  padding: 12px 14px
  border-radius: 8px

.heslb-export__alert-title
  margin-bottom: 6px
  font-weight: 700

.heslb-export__alert--warning
  margin-top: 14px
  border: 1px solid #f3d29a
  background: #fff4dc
  color: #8a5a00

.heslb-export__alert--error
  border: 1px solid #f3b4b4
  background: #fbe1e1
  color: #c84a4a

.heslb-export__warnings
  margin: 0
  padding-left: 18px

  li + li
    margin-top: 4px

@media (max-width: 767px)
  .heslb-export__body
    grid-template-columns: 1fr
</style>
